import { useState } from 'react';
import { Text, Input, Spinner, tokens } from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';
import { fetchChatFeedbackCount } from '../../api/admin.api';
import { useT } from '../../i18n';

const DAY_MS = 86_400_000;

/** Rango por defecto: últimos 30 días en días completos UTC (igual que el backend). */
export function defaultChatFeedbackRange(now: Date = new Date()): { from: string; to: string } {
  return {
    from: new Date(now.getTime() - 30 * DAY_MS).toISOString().slice(0, 10),
    to: now.toISOString().slice(0, 10),
  };
}

/** Totales de 👍 y 👎 del chat web de la empresa, con rango de fechas editable. */
export function ChatFeedbackSection() {
  const ts = useT().settings;
  const [range, setRange] = useState(() => defaultChatFeedbackRange());

  const { data, isLoading, isError } = useQuery({
    queryKey: ['chat-feedback-count', range.from, range.to],
    queryFn: () => fetchChatFeedbackCount(range.from, range.to),
    staleTime: 60_000,
    retry: false,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <Text weight="semibold" style={{ fontSize: '12px' }}>{ts.chatFeedbackTitle}</Text>

      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px' }}>
          {ts.chatFeedbackFrom}
          <Input
            type="date"
            size="small"
            value={range.from}
            max={range.to}
            onChange={(_, d) => setRange(r => ({ ...r, from: d.value }))}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px' }}>
          {ts.chatFeedbackTo}
          <Input
            type="date"
            size="small"
            value={range.to}
            min={range.from}
            onChange={(_, d) => setRange(r => ({ ...r, to: d.value }))}
          />
        </label>
      </div>

      {isLoading && <Spinner size="small" />}
      {isError && <Text style={{ color: tokens.colorStatusDangerForeground1 }}>{ts.chatFeedbackLoadError}</Text>}

      {data && (
        <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
          <div>
            <Text data-testid="chat-feedback-up" weight="semibold" style={{ fontSize: '20px', display: 'block' }}>{data.up}</Text>
            <Text style={{ fontSize: '12px', color: tokens.colorNeutralForeground3 }}>{ts.chatFeedbackUp}</Text>
          </div>
          <div>
            <Text data-testid="chat-feedback-down" weight="semibold" style={{ fontSize: '20px', display: 'block' }}>{data.down}</Text>
            <Text style={{ fontSize: '12px', color: tokens.colorNeutralForeground3 }}>{ts.chatFeedbackDown}</Text>
          </div>
        </div>
      )}
    </div>
  );
}
